import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDashboardUi } from '../../config/dashboardUiTheme';
import { cn } from '../../lib/utils';
import { useRiskAssessments } from '../../hooks/useEntities';

const RiskMatrix = ({ documents = [] }) => {
  const { theme } = useTheme();
  const dash = useDashboardUi(theme);
  
  // Fetch actual risk assessment data
  const { data: riskAssessments = [] } = useRiskAssessments();

  /** Map DB 1–10 scores to matrix axis 1–5. */
  const scaleToMatrixAxis = (value, max = 10) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return 3;
    return Math.min(5, Math.max(1, Math.ceil((n / max) * 5)));
  };

  // Transform risk assessment records → 5×5 probability × impact matrix
  const riskData = useMemo(() => {
    return riskAssessments.map((risk) => {
      const probability = scaleToMatrixAxis(risk.levelOfRisk);
      const impact = scaleToMatrixAxis(risk.impact);
      const description =
        [risk.parameter, risk.effect, risk.rationale].filter(Boolean).join(' — ') ||
        '—';
      const owner =
        risk.createdBy?.userDisplayName ||
        risk.createdBy?.userEmail ||
        risk.updatedBy?.userDisplayName ||
        '—';

      return {
        id: risk.id,
        title: risk.title || risk.code || `Risk #${risk.id}`,
        description,
        probability,
        impact,
        result: risk.result,
        category: risk.riskCategory?.name || 'General',
        status: risk.status?.name || '—',
        owner,
        nextReviewDate: risk.requestedDate || risk.receivedDate,
      };
    });
  }, [riskAssessments]);

  const getRiskLevel = (probability, impact) => {
    const score = probability * impact;
    if (score >= 16) return { level: 'Critical', semantic: 'danger' };
    if (score >= 12) return { level: 'High', semantic: 'warning' };
    if (score >= 6) return { level: 'Medium', semantic: 'info' };
    return { level: 'Low', semantic: 'success' };
  };

  const legendScoreForLevel = (level) => {
    if (level === 'Critical') return { probability: 5, impact: 4 };
    if (level === 'High') return { probability: 4, impact: 3 };
    if (level === 'Medium') return { probability: 3, impact: 3 };
    return { probability: 2, impact: 2 };
  };

  const riskCounts = useMemo(() => {
    const counts = { Critical: 0, High: 0, Medium: 0, Low: 0 };
    riskData.forEach(risk => {
      const { level } = getRiskLevel(risk.probability, risk.impact);
      counts[level]++;
    });
    return counts;
  }, [riskData]);

  const getMatrixCellColor = (probability, impact) => {
    const { semantic } = getRiskLevel(probability, impact);
    return cn(dash.fill(semantic), 'bg-opacity-20');
  };

  const getMatrixCellRisks = (probability, impact) => {
    return riskData.filter(risk => risk.probability === probability && risk.impact === impact);
  };

  return (
    <div className={`${dash.widgetShell()}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className={`text-xl font-semibold ${theme.text.primary}`}>
          Risk Matrix
        </h3>
        <div className="flex items-center gap-4">
          {Object.entries(riskCounts).map(([level, count]) => {
            const { semantic } = getRiskLevel(
              legendScoreForLevel(level).probability,
              legendScoreForLevel(level).impact,
            );
            return (
              <div key={level} className="flex items-center gap-2">
                <span className={cn('w-3 h-3 rounded-full shrink-0', dash.dot(semantic))} aria-hidden />
                <span className={cn('text-sm', theme.text.secondary)}>
                  {level}: {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-stretch">
        {/* Risk Matrix */}
        <div className="lg:col-span-2">
          <h4 className={`text-lg font-medium ${theme.text.primary} mb-4`}>
            Probability vs Impact Matrix
          </h4>
          
          <div className="relative">
            {/* Matrix Grid */}
            <div className="grid grid-cols-6 gap-1">
              {/* Header row */}
              <div className={`p-2 text-center text-xs font-medium ${theme.text.secondary}`}></div>
              {[1, 2, 3, 4, 5].map(impact => (
                <div key={impact} className={`p-2 text-center text-xs font-medium ${theme.text.secondary}`}>
                  {impact}
                </div>
              ))}
              
              {/* Matrix cells */}
              {[5, 4, 3, 2, 1].map(probability => (
                <React.Fragment key={probability}>
                  <div className={`p-2 text-center text-xs font-medium ${theme.text.secondary}`}>
                    {probability}
                  </div>
                  {[1, 2, 3, 4, 5].map(impact => {
                    const cellRisks = getMatrixCellRisks(probability, impact);
                    return (
                      <div
                        key={`${probability}-${impact}`}
                        className={`aspect-square p-2 rounded-lg border ${theme.border.light} ${getMatrixCellColor(probability, impact)} flex items-center justify-center relative group cursor-pointer`}
                        title={cellRisks.map(r => r.title).join(', ') || 'No risks'}
                      >
                        {cellRisks.length > 0 && (
                          <span className={`text-xs font-bold ${theme.text.primary}`}>
                            {cellRisks.length}
                          </span>
                        )}
                        
                        {/* Tooltip */}
                        {cellRisks.length > 0 && (
                          <div
                            className={cn(
                              'absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 p-2 border rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap max-w-xs',
                              theme.colors.surface,
                              theme.border.default,
                              theme.shadow.lg,
                            )}
                          >
                            <div className={`text-xs ${theme.text.primary} font-medium`}>
                              {cellRisks.map(r => r.title).join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
            
            {/* Axis labels */}
            <div className={`absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-sm font-medium ${theme.text.secondary}`}>
              Impact →
            </div>
            <div className={`absolute top-1/2 -left-12 transform -translate-y-1/2 -rotate-90 text-sm font-medium ${theme.text.secondary}`}>
              Probability →
            </div>
          </div>
        </div>

        {/* Risk List */}
        <div className="flex flex-col min-h-0 lg:min-h-[28rem]">
          <h4 className={`text-lg font-medium ${theme.text.primary} mb-4 shrink-0`}>
            Top Risks
          </h4>
          
          <div className="space-y-3 flex-1 min-h-[28rem] max-h-[36rem] lg:max-h-[40rem] overflow-y-auto advanced-table-scroll pr-1">
            {riskData
              .sort((a, b) => (b.probability * b.impact) - (a.probability * a.impact))
              .slice(0, 10)
              .map(risk => {
                const { level, semantic } = getRiskLevel(risk.probability, risk.impact);
                return (
                  <div
                    key={risk.id}
                    className={cn(
                      'p-3 rounded-lg transition-colors duration-200 border',
                      dash.listItem(),
                      theme.border.light,
                    )}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h5 className={cn('text-sm font-medium truncate', theme.text.primary)}>
                        {risk.title}
                      </h5>
                      <span className={dash.badge(semantic, 'text-xs shrink-0')}>{level}</span>
                    </div>

                    <p className={cn('text-xs mb-2 line-clamp-2', theme.text.secondary)}>
                      {risk.description}
                    </p>

                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className={cn('truncate', theme.text.muted)}>{risk.owner}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className={dash.badge('secondary', 'text-[10px] px-1.5 py-0.5 tabular-nums')}>
                          P:{risk.probability}
                        </span>
                        <span className={dash.badge('secondary', 'text-[10px] px-1.5 py-0.5 tabular-nums')}>
                          I:{risk.impact}
                        </span>
                      </div>
                    </div>
                    {risk.nextReviewDate && (
                      <div className="mt-2 text-xs">
                        <span className={`${theme.text.muted}`}>
                          Next Review: {new Date(risk.nextReviewDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Risk Summary */}
      <div className={`mt-6 pt-4 border-t ${theme.border.light}`}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center">
            <div className={`text-2xl font-bold ${dash.text('danger')}`}>
              {riskCounts.Critical + riskCounts.High}
            </div>
            <div className={`text-xs ${theme.text.muted}`}>High Priority Risks</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${theme.text.primary}`}>
              {
                riskData.filter((r) => {
                  const s = String(r.status || '').toLowerCase();
                  return s && !s.includes('closed') && !s.includes('mitigat');
                }).length
              }
            </div>
            <div className={`text-xs ${theme.text.muted}`}>Open Risks</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${dash.text('success')}`}>
              {
                riskData.filter((r) => {
                  const s = String(r.status || '').toLowerCase();
                  return s.includes('closed') || s.includes('mitigat');
                }).length
              }
            </div>
            <div className={`text-xs ${theme.text.muted}`}>Closed / Mitigated</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${theme.text.primary}`}>
              {riskData.length === 0
                ? '—'
                : `${Math.round(
                    (riskData.filter((r) => {
                      const s = String(r.status || '').toLowerCase();
                      return s.includes('closed') || s.includes('mitigat');
                    }).length /
                      riskData.length) *
                      100,
                  )}%`}
            </div>
            <div className={`text-xs ${theme.text.muted}`}>Closure Rate</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskMatrix;

